/*
 * The route guard for every authenticated admin route (flows/admin-auth). Four gates, in order:
 * a session still being resolved → hold; no token → /login; a pending forced reset →
 * /reset-password (the must-reset operator cannot reach any other route until the flag clears);
 * otherwise render the matched route. It is a layout route, so it wraps its children via <Outlet />.
 *
 * The hold exists because a tab opened with cmd-click starts with an empty sessionStorage and has to
 * ask the other tabs for the token (CAE-576). Redirecting during that window would not merely flash
 * the login page — `replace` would discard the URL the operator opened, so the token arriving a
 * moment later would leave them signed in on the wrong screen.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../session/admin-auth-context';

export function RequireAuth() {
  const { claims, isResolving } = useAdminAuth();

  if (isResolving) {
    // Deliberately blank: the wait is a few hundred milliseconds at most, and a spinner that
    // appears and vanishes inside that window reads as a glitch rather than as progress.
    return <div data-testid="session-resolving" aria-busy="true" />;
  }
  if (claims === null) {
    return <Navigate to="/login" replace />;
  }
  if (claims.mustResetPw) {
    return <Navigate to="/reset-password" replace />;
  }
  return <Outlet />;
}
